import { Component, ChangeDetectionStrategy, signal, WritableSignal } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { AuthService } from "../../service/auth.service";
import { GeneralService } from "../../service/general.service";
import { User } from "../../entity/User";
import { Router } from "@angular/router";
import { Company } from "../../entity/Company";
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from "rxjs";
import { Pair } from "../../obj/Pair";

@Component({
    selector: "user-company-selector-dialog",
    templateUrl: "../../../html/incs/user-company-selector-dialog.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule]
})
export class UserCompanySelectorDialog {
    public confirmMessage: string | null = null;
    public confirmTitle: string = "Confirm";

    query: WritableSignal<string> = signal('');
    showResidential: WritableSignal<boolean> = signal(false);
    showCommercial: WritableSignal<boolean> = signal(false);

    searchAutocompleteItems: WritableSignal<Pair[]> = signal([]);
    autocompleteCurrentPosition: WritableSignal<number> = signal(0);
    isVisibleAutocomplete: WritableSignal<boolean> = signal(true);

    userCompanies: (Company|null)[];
    user: User = new User();
    userCompany: Company | null = null;

    constructor(
        public dialogRef: MatDialogRef<UserCompanySelectorDialog>,
        private authService: AuthService,
        private generalService: GeneralService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.generalService.getDetails()
            .then(u => {
                this.user = u;
                if (this.user == null) {
                    this.dialogRef.close(null!);
                }
            });
        firstValueFrom(this.generalService.getUsersActiveCompanies()).then(companies => {
            this.userCompanies = companies;
        });
    }

    // Handlers for checkbox toggles
    toggleResidential(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.showResidential.set(checked);
        this.searchSiteInputChanged(event);
    }

    toggleCommercial(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.showCommercial.set(checked);
        this.searchSiteInputChanged(event);
    }

    // Handler for text input
    onQueryInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.query.set(value);
        this.searchSiteInputChanged(event);
    }

    searchFocus(): void {
        this.isVisibleAutocomplete.set(true);
    }

    searchFocusOut(): void {
        // Keep original behavior (no change)
    }

    searchSiteByKey(event: any): void {
        if (event.keyCode == 13) {
            const items = this.searchAutocompleteItems();
            const currentPos = this.autocompleteCurrentPosition();
            if (items && items[currentPos - 1]) {
                this.changeCompany(items[currentPos - 1].key);
            }
        }
        if (event.keyCode == 38) { // up
            const itemsLength = this.searchAutocompleteItems().length;
            if (itemsLength > 0) {
                let newPos = this.autocompleteCurrentPosition() - 1;
                if (newPos <= 0) {
                    newPos = itemsLength;
                }
                this.autocompleteCurrentPosition.set(newPos);
            }
        }
        if (event.keyCode == 40) { // down
            const itemsLength = this.searchAutocompleteItems().length;
            if (itemsLength > 0) {
                let newPos = this.autocompleteCurrentPosition() + 1;
                if (newPos > itemsLength) {
                    newPos = 1;
                }
                this.autocompleteCurrentPosition.set(newPos);
            }
        }
    }

    searchSiteInputChanged(event: any): void {
        this.isVisibleAutocomplete.set(true);
        let queryValue = this.query();
        if (queryValue == null) {
            queryValue = "";
            this.query.set("");
        }
        let residentialFlag = this.showResidential();
        let commercialFlag = this.showCommercial();
        if (!residentialFlag && !commercialFlag && queryValue.length > 0) {
            residentialFlag = true;
            commercialFlag = true;
            this.showResidential.set(true);
            this.showCommercial.set(true);
        }
        let data = [];
        for (let i = 0; i < this.userCompanies.length; i++) {
            let name = (this.userCompanies[i] == null) ? (this.user.firstName + " " + this.user.lastName) : this.userCompanies[i]!.name;
            let isCommercial = !(this.userCompanies[i] == null);
            if ((isCommercial && commercialFlag) || (!isCommercial && residentialFlag)) {
                if (queryValue == '' || name!.toLocaleLowerCase().includes(queryValue.toLocaleLowerCase())) {
                    data.push(new Pair(new Pair(!(this.userCompanies[i] == null), this.userCompanies[i]), name));
                }
            }
        }
        this.autocompleteCurrentPosition.set(0);
        this.searchAutocompleteItems.set(data);
    }

    getSearchAutocompleteClass(): string {
        const visible = this.isVisibleAutocomplete() && this.searchAutocompleteItems().length > 0;
        return "clients-autocomplete " + (visible ? "visible" : "hidden");
    }

    getAutocompleteClassItem(pos: number): string {
        if (pos == this.autocompleteCurrentPosition() - 1) {
            return "selected";
        }
        return "";
    }

    changeCompany(data: Pair): void {
        this.generalService.switchStoredUserFilter(data.key, data.value);
        this.generalService.clearStoredAccount();
        this.dialogRef.close(true);
    }

}